import type { DatePrecision, IntendedAction, JobScheduleDraft } from "../types";

/**
 * Schedule Intent domain rules — the single source of truth for the action set,
 * for how an approximate date is normalised, and for how either is worded.
 *
 * Everything that touches a Schedule Intent imports from here: the form, the
 * board card, and the server-side client projection. Nothing formats a stored
 * anchor date itself (DEC-JS-005) and nothing inlines the action list
 * (DEC-JS-003), so adding a value is a one-file change that the type system
 * then forces every call site to handle.
 */

/** Board column that means "parked"; the intent advises it, never moves it (DEC-JS-004). */
export const HOLD_COLUMN_ID = "hold";

/** Longest accepted internal note, matching the timing reason's limit. */
const NOTE_MAX_LENGTH = 1000;

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * One Intended Action and everything that follows from choosing it.
 *
 * `waiting` is a property of the value rather than a second stored field, so
 * "what is stuck, and on what" stays a single-column query. `clientLabel` is
 * the wording a client portal user sees; several actions deliberately collapse
 * onto one label, because an internally honest value ("we have not found a
 * subcontractor") is not always a helpful thing to publish (DEC-JS-006).
 */
export interface IntendedActionSpec {
  value: IntendedAction;
  waiting: boolean;
  clientLabel: string;
}

export const INTENDED_ACTIONS: readonly IntendedActionSpec[] = [
  { value: "Inspect / quote", waiting: false, clientLabel: "Inspection planned" },
  { value: "Attend site", waiting: false, clientLabel: "Attendance planned" },
  { value: "Return to complete", waiting: false, clientLabel: "Return visit planned" },
  { value: "Awaiting quote approval", waiting: true, clientLabel: "Awaiting your approval" },
  { value: "Awaiting parts", waiting: true, clientLabel: "Awaiting parts" },
  { value: "Awaiting access", waiting: true, clientLabel: "Awaiting site access" },
  { value: "Awaiting trade", waiting: true, clientLabel: "Scheduling in progress" },
  { value: "Awaiting third-party decision", waiting: true, clientLabel: "Scheduling in progress" },
];

export const DATE_PRECISIONS: readonly DatePrecision[] = ["day", "week", "month"];

const SPEC_BY_VALUE = new Map(INTENDED_ACTIONS.map((spec) => [spec.value, spec]));

/** The spec for an action, or undefined for an unplanned job / unknown value. */
export function intendedActionSpec(action: IntendedAction | "" | null): IntendedActionSpec | undefined {
  return action ? SPEC_BY_VALUE.get(action) : undefined;
}

/** True when the job is waiting on something rather than progressing. */
export function isWaiting(action: IntendedAction | "" | null): boolean {
  return intendedActionSpec(action)?.waiting ?? false;
}

/**
 * The sanitised wording for a client portal reader, or "" when there is nothing
 * to publish. Never returns the raw action.
 */
export function clientLabelFor(action: IntendedAction | "" | null): string {
  return intendedActionSpec(action)?.clientLabel ?? "";
}

/** Parses a `YYYY-MM-DD` anchor as UTC midnight, so no time zone can shift the day. */
function parseAnchor(on: string): Date | null {
  if (!DATE_PATTERN.test(on)) return null;
  const [year, month, day] = on.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  // Rejects real-looking nonsense such as 2026-02-31, which the pattern allows.
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
    ? date
    : null;
}

function toAnchorString(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/**
 * Snaps an anchor to the start of the period its precision describes: the
 * Monday of that week, or the first of that month. Applied on every write so
 * two "week of the 14th" intents are the same value and date ordering stays
 * meaningful. Returns "" for an unparseable date.
 */
export function normaliseAnchor(on: string, precision: DatePrecision | ""): string {
  const date = parseAnchor(on);
  if (!date || !precision) return "";
  if (precision === "month") {
    return toAnchorString(new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1)));
  }
  if (precision === "week") {
    // getUTCDay() is 0 on Sunday, which belongs to the week that started six
    // days earlier rather than the one starting tomorrow.
    const weekday = date.getUTCDay();
    const backToMonday = weekday === 0 ? 6 : weekday - 1;
    date.setUTCDate(date.getUTCDate() - backToMonday);
  }
  return toAnchorString(date);
}

const DAY_FORMAT = new Intl.DateTimeFormat("en-AU", {
  weekday: "short",
  day: "numeric",
  month: "short",
  timeZone: "UTC",
});
const DAY_MONTH_FORMAT = new Intl.DateTimeFormat("en-AU", {
  day: "numeric",
  month: "short",
  timeZone: "UTC",
});
const MONTH_FORMAT = new Intl.DateTimeFormat("en-AU", { month: "long", timeZone: "UTC" });

/**
 * The only renderer of a stored anchor. Precision decides the wording, so a
 * month-precision guess can never read as a specific day — which is what keeps
 * an internal estimate from becoming a client's expectation (DEC-JS-005).
 *
 * The year is appended only when it differs from `reference`, so the common
 * case stays short without ever being ambiguous across a year boundary.
 *
 * @param {string} on - Stored `YYYY-MM-DD` anchor.
 * @param {DatePrecision | ""} precision - How well the date is known.
 * @param {Date} [reference] - "Today", injectable so the output is testable.
 * @returns {string} Human wording, or "" when there is no usable date.
 */
export function formatApproximateDate(
  on: string,
  precision: DatePrecision | "",
  reference: Date = new Date(),
): string {
  const date = parseAnchor(on);
  if (!date || !precision) return "";
  const year = date.getUTCFullYear();
  const suffix = year === reference.getFullYear() ? "" : ` ${year}`;
  if (precision === "month") return `${MONTH_FORMAT.format(date)}${suffix}`;
  if (precision === "week") return `week of ${DAY_MONTH_FORMAT.format(date)}${suffix}`;
  return `${DAY_FORMAT.format(date)}${suffix}`;
}

/**
 * Disagreement between the typed intent and where the card actually sits. The
 * board is never moved automatically (DEC-JS-004); this only supplies the
 * marker that makes the disagreement visible.
 */
export type ScheduleStageMismatch = "waiting-outside-hold" | "progressing-in-hold";

export function stageMismatch(
  action: IntendedAction | "" | null,
  columnId: string,
): ScheduleStageMismatch | null {
  const spec = intendedActionSpec(action);
  if (!spec) return null;
  if (spec.waiting && columnId !== HOLD_COLUMN_ID) return "waiting-outside-hold";
  if (!spec.waiting && columnId === HOLD_COLUMN_ID) return "progressing-in-hold";
  return null;
}

export const STAGE_MISMATCH_LABELS: Record<ScheduleStageMismatch, string> = {
  "waiting-outside-hold": "Waiting, but not on Hold",
  "progressing-in-hold": "On Hold, but not waiting",
};

/** An empty Schedule Intent: the state of a job nobody has planned yet. */
export function emptyJobSchedule(): JobScheduleDraft {
  return { action: "", intendedOn: "", precision: "", internalNote: "", updatedAt: null, updatedBy: null };
}

/** True when nothing has been entered, so the page can be skipped on save. */
export function isEmptyJobSchedule(draft: JobScheduleDraft): boolean {
  return !draft.action && !draft.intendedOn && !draft.precision && !draft.internalNote.trim();
}

/**
 * Page validation, deliberately light in the manner of the Scope page: an
 * optional page must never block the base job. Only a value the user actually
 * started entering has to be coherent — an action with no date is a perfectly
 * good intent, and so is a date with no action.
 */
export function validateJobSchedule(draft: JobScheduleDraft): string {
  if (Boolean(draft.intendedOn) !== Boolean(draft.precision)) {
    return draft.intendedOn
      ? "Choose how precisely the schedule date is known"
      : "Add the schedule date, or clear its precision";
  }
  if (draft.intendedOn && !parseAnchor(draft.intendedOn)) {
    return "Enter a valid schedule date";
  }
  if (draft.internalNote.trim().length > NOTE_MAX_LENGTH) {
    return "Internal schedule note must be 1,000 characters or fewer";
  }
  return "";
}

/**
 * The draft as it should be persisted: the anchor snapped to its precision and
 * text trimmed. Returns null when nothing was entered, so an untouched page
 * writes no row.
 */
export function toJobSchedulePayload(draft: JobScheduleDraft): {
  action: IntendedAction | null;
  intendedOn: string | null;
  precision: DatePrecision | null;
  internalNote: string | null;
} | null {
  if (isEmptyJobSchedule(draft)) return null;
  const intendedOn = draft.precision ? normaliseAnchor(draft.intendedOn, draft.precision) : "";
  return {
    action: draft.action || null,
    intendedOn: intendedOn || null,
    precision: intendedOn ? (draft.precision as DatePrecision) : null,
    internalNote: draft.internalNote.trim() || null,
  };
}
